import React from 'react';
import { fromGlobalId } from 'graphql-relay';
import Styled from 'styled';
import Helmet, { Props as HelmetProps } from 'helmet/helmet';
import { Formik, FieldProps, Form, Field } from 'formik';
import Route from 'react-router/route';
import { Mutation } from '@apollo/client/react/components';
import { Editor as TinymceEditor } from 'tinymce';
import { FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router-dom';
import classnames from 'classnames';

import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';

import { mergeValidators, emptyValidator } from 'four-nets/ui-v2/form/validators';
import storySvg from 'four-nets/icons/svg/icon_story.svg';
import photoIcon from 'four-nets/icons/svg/icon_photo.svg';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import VisibilitySelect from 'four-nets/photoblog/create-post/visibility-select/visibility-select';
import SubmitButton from 'four-nets/photoblog/create-post/submit-button/submit-button';
import UploadPhotofile, { UploadedPhotoFile } from 'four-nets/ui-v2/form/upload-photofile/upload-photofile';
import Input from 'four-nets/ui-v2/form/input/input';
import { AlertContext } from 'lib/alert-context/alert-context';
import LeftMenu from 'four-nets/photoblog/left-menu/left-menu';

import './create-edit-article.graphql';
import CreateArticle, {
    CreateArticleMutation,
    CreateArticleMutationVariables,
} from './mutations/create-article.graphql';
import { PostVisibility, PostType } from './mutations/create-article.graphql.types';
import EditArticle, { EditArticleMutation, EditArticleMutationVariables } from './mutations/edit-article.graphql';
import { CreateEditArticleFragment } from './create-edit-article.graphql';
import Editor, { Props as EditorProps } from './editor/editor';
import defaultStyles from './create-edit-article.scss';

export interface Props {
    styles: typeof defaultStyles;
    article?: CreateEditArticleFragment;
}

interface Values {
    title: string;
    content: string;
    visibility: PostVisibility;
    featuredImage?: UploadedPhotoFile | null;
}

export interface State {
    key: number;
    file?: File;
    editor: TinymceEditor | null;
    initialValues: Values;
}

type Errors = Partial<{ [key in keyof Values]: Array<React.ReactElement<FormattedMessage>> | true }>;

class CreateEditArticle extends React.PureComponent<Props, State> {
    state: State;
    formik!: FormikProps<Values>;

    constructor(props: Props) {
        super(props);
        const { article } = props;

        this.state = {
            key: 0,
            editor: null,
            initialValues: {
                title: article && article.title ? article.title : '',
                content: article && article.content ? article.content : '',
                featuredImage: (article && article.titleImage) || undefined,
                visibility:
                    article && article.visibility ? article.visibility : PostVisibility.POST_VISIBILITY_AUTHENTICATED,
            },
        };
    }

    renderHelmet: HelmetProps['render'] = H => {
        const children = [];

        if (this.state.initialValues.featuredImage) {
            const featuredImage = this.state.initialValues.featuredImage;

            children.push(
                <meta property='og:image' content={featuredImage.src} />,
                <meta property='og:image:width' content={`${featuredImage.width!}`} />,
                <meta property='og:image:height' content={`${featuredImage.height!}`} />
            );
        }

        return (
            <React.Fragment>
                <H>{children}</H>
                <FormattedMessage id='ADD_ARTICLE' values={{ gender: 'v' }}>
                    {msg => (
                        <H>
                            {this.state.initialValues.title ? (
                                <title>{this.state.initialValues.title}</title>
                            ) : (
                                <title>{msg}</title>
                            )}
                        </H>
                    )}
                </FormattedMessage>
            </React.Fragment>
        );
    };

    setContent = () => {
        const content = this.state.editor && this.state.editor.getContent();
        this.formik.setFieldValue('content', content || '');
    };

    setEditor: EditorProps['forwardedRef'] = editor => {
        this.setState({
            editor,
        });
    };

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (!prevState.editor && this.state.editor) {
            if (this.formik.values.content) {
                this.state.editor.setContent(this.formik.values.content);
            }

            this.state.editor.on('Change', this.setContent);
        }
    }

    componentWillUnmount() {
        if (this.state.editor) {
            this.state.editor.off('Change', this.setContent);
        }
    }

    render() {
        const { styles, article } = this.props;

        const isEditing = article !== null && article !== undefined;

        return (
            <Route
                render={({ history }) => (
                    <AlertContext.Consumer>
                        {({ acknowledge }) => (
                            <React.Fragment>
                                <h1 className={styles.mainTitle}>
                                    {isEditing ? (
                                        <FormattedMessage id='EDIT_ARTICLE' />
                                    ) : (
                                        <FormattedMessage id='ADD_ARTICLE' values={{ gender: 'v' }} />
                                    )}
                                </h1>
                                <div className={styles.columns}>
                                    <Helmet render={this.renderHelmet} />
                                    <LeftMenu>
                                        <ViewerConsumer>
                                            {({ viewer }) =>
                                                viewer ? (
                                                    <NavLink
                                                        to={`/blog/${viewer.username}/add/article/`}
                                                        className={classnames(styles.navLink, styles.menuOpen)}
                                                        activeClassName={styles.active}
                                                        isActive={() => true}
                                                    >
                                                        <SVGIcon src={storySvg} className={styles.navIcon} />
                                                        <FormattedMessage id='ADD_ARTICLE' values={{ gender: 'v' }} />
                                                    </NavLink>
                                                ) : null
                                            }
                                        </ViewerConsumer>
                                    </LeftMenu>
                                    <ViewerConsumer>
                                        {({ viewer }) =>
                                            !viewer ? (
                                                <Unauthorized />
                                            ) : (
                                                <Mutation<CreateArticleMutation, CreateArticleMutationVariables>
                                                    mutation={CreateArticle}
                                                >
                                                    {createArticle => (
                                                        <Mutation<EditArticleMutation, EditArticleMutationVariables>
                                                            mutation={EditArticle}
                                                        >
                                                            {editArticle => (
                                                                <div className={styles.container}>
                                                                    <Formik<Values>
                                                                        initialValues={this.state.initialValues}
                                                                        validate={values => {
                                                                            const errors: Errors = {};

                                                                            const titleErrors = mergeValidators([
                                                                                emptyValidator,
                                                                            ])(values.title);

                                                                            if (titleErrors) {
                                                                                errors.title = titleErrors;
                                                                            }

                                                                            if (
                                                                                this.state.file &&
                                                                                !values.featuredImage
                                                                            ) {
                                                                                errors.featuredImage = true;
                                                                            }

                                                                            const contentErrors = mergeValidators([
                                                                                emptyValidator,
                                                                            ])(values.content);

                                                                            if (contentErrors) {
                                                                                errors.content = contentErrors;
                                                                            }

                                                                            return errors;
                                                                        }}
                                                                        onSubmit={(values, actions) => {
                                                                            let featuredImage:
                                                                                | number
                                                                                | null
                                                                                | undefined;
                                                                            if (values.featuredImage) {
                                                                                featuredImage = parseInt(
                                                                                    fromGlobalId(
                                                                                        values.featuredImage.id
                                                                                    ).id,
                                                                                    10
                                                                                );
                                                                            } else if (values.featuredImage === null) {
                                                                                featuredImage = null;
                                                                            }

                                                                            const { content, title, visibility } =
                                                                                values;

                                                                            const input = {
                                                                                id: article ? article.id : null,
                                                                                title,
                                                                                content,
                                                                                postType: 'POST_ARTICLE',
                                                                                titleImageId: featuredImage,
                                                                                visibility,
                                                                            };

                                                                            actions.setSubmitting(true);

                                                                            let promise: any;

                                                                            if (isEditing) {
                                                                                promise = editArticle({
                                                                                    variables: {
                                                                                        input,
                                                                                    },
                                                                                }).then(data => {
                                                                                    if (
                                                                                        data &&
                                                                                        data.data &&
                                                                                        data.data.updatePost &&
                                                                                        data.data.updatePost.post &&
                                                                                        data.data.updatePost.post.user
                                                                                    ) {
                                                                                        const { slug } =
                                                                                            data.data.updatePost.post;
                                                                                        const { username } =
                                                                                            data.data.updatePost.post
                                                                                                .user;

                                                                                        return { slug, username };
                                                                                    }
                                                                                });
                                                                            } else {
                                                                                promise = createArticle({
                                                                                    variables: {
                                                                                        input,
                                                                                    },
                                                                                }).then(data => {
                                                                                    if (
                                                                                        data &&
                                                                                        data.data &&
                                                                                        data.data.createPost &&
                                                                                        data.data.createPost.post &&
                                                                                        data.data.createPost.post.user
                                                                                    ) {
                                                                                        const { slug } =
                                                                                            data.data.createPost.post;
                                                                                        const { username } =
                                                                                            data.data.createPost.post
                                                                                                .user;

                                                                                        return { slug, username };
                                                                                    }
                                                                                });
                                                                            }

                                                                            promise
                                                                                .then(
                                                                                    ({ slug, username }: any = {}) => {
                                                                                        if (slug && username) {
                                                                                            history.push(
                                                                                                `/blog/${username}/article/${slug}/`
                                                                                            );
                                                                                        } else {
                                                                                            history.push(`/blogs/`);
                                                                                        }
                                                                                    }
                                                                                )

                                                                                .catch((err: Error) => {
                                                                                    acknowledge({ body: err.message });
                                                                                })
                                                                                .finally(() => {
                                                                                    actions.setSubmitting(false);
                                                                                });
                                                                        }}
                                                                    >
                                                                        {formik => {
                                                                            this.formik = formik;

                                                                            const {
                                                                                validateForm,
                                                                                values,
                                                                                setFieldValue,
                                                                                isSubmitting,
                                                                                isValid,
                                                                                submitForm,
                                                                                touched,
                                                                                errors,
                                                                            } = formik;

                                                                            return (
                                                                                <Form className={styles.form}>
                                                                                    <Field name='title'>
                                                                                        {({ field }: FieldProps) => (
                                                                                            <label
                                                                                                className={styles.title}
                                                                                            >
                                                                                                <FormattedMessage id='ARTICLE_TITLE'>
                                                                                                    {msg => (
                                                                                                        <span
                                                                                                            className={
                                                                                                                styles.label
                                                                                                            }
                                                                                                        >
                                                                                                            {msg}
                                                                                                        </span>
                                                                                                    )}
                                                                                                </FormattedMessage>
                                                                                                <Input
                                                                                                    errors={
                                                                                                        errors.title
                                                                                                            ? [
                                                                                                                  errors.title,
                                                                                                              ]
                                                                                                            : undefined
                                                                                                    }
                                                                                                    touched={
                                                                                                        touched.title
                                                                                                    }
                                                                                                    {...field}
                                                                                                />
                                                                                            </label>
                                                                                        )}
                                                                                    </Field>
                                                                                    <FormattedMessage id='ARTICLE_TEXT'>
                                                                                        {msg => (
                                                                                            <span
                                                                                                className={styles.label}
                                                                                            >
                                                                                                {msg}
                                                                                            </span>
                                                                                        )}
                                                                                    </FormattedMessage>
                                                                                    <Editor
                                                                                        ref={this.setEditor}
                                                                                        viewer={viewer}
                                                                                    />
                                                                                    <div className={styles.bottom}>
                                                                                        <Field name='featuredImage'>
                                                                                            {() => (
                                                                                                <label
                                                                                                    className={
                                                                                                        styles.featuredImage
                                                                                                    }
                                                                                                >
                                                                                                    <SVGIcon
                                                                                                        src={photoIcon}
                                                                                                        className={
                                                                                                            styles.icon
                                                                                                        }
                                                                                                    />
                                                                                                    {values.featuredImage ? (
                                                                                                        <FormattedMessage id='CHANGE_TITLE_IMAGE' />
                                                                                                    ) : (
                                                                                                        <FormattedMessage id='ADD_TITLE_IMAGE' />
                                                                                                    )}
                                                                                                    <input
                                                                                                        key={
                                                                                                            this.state
                                                                                                                .key
                                                                                                        }
                                                                                                        className={
                                                                                                            styles.input
                                                                                                        }
                                                                                                        type='file'
                                                                                                        onChange={event => {
                                                                                                            setFieldValue(
                                                                                                                'featuredImage',
                                                                                                                null
                                                                                                            );
                                                                                                            if (
                                                                                                                event
                                                                                                                    .target
                                                                                                                    .files!
                                                                                                                    .length
                                                                                                            ) {
                                                                                                                this.setState(
                                                                                                                    {
                                                                                                                        file: event
                                                                                                                            .target
                                                                                                                            .files![0],
                                                                                                                    }
                                                                                                                );
                                                                                                            }
                                                                                                        }}
                                                                                                    />
                                                                                                </label>
                                                                                            )}
                                                                                        </Field>
                                                                                        {this.state.file ||
                                                                                        values.featuredImage ? (
                                                                                            <UploadPhotofile
                                                                                                uploadStyle={{
                                                                                                    upload: styles.upload,
                                                                                                }}
                                                                                                automaticUpload={true}
                                                                                                key={
                                                                                                    this.state.file &&
                                                                                                    this.state.file.name
                                                                                                }
                                                                                                file={this.state.file}
                                                                                                photoFile={
                                                                                                    values.featuredImage ||
                                                                                                    undefined
                                                                                                }
                                                                                                onClose={() => {
                                                                                                    setFieldValue(
                                                                                                        'featuredImage',
                                                                                                        null
                                                                                                    );

                                                                                                    this.setState(
                                                                                                        state => ({
                                                                                                            key:
                                                                                                                state.key +
                                                                                                                1,
                                                                                                            file: undefined,
                                                                                                        }),
                                                                                                        validateForm
                                                                                                    );
                                                                                                }}
                                                                                                onPhotoFile={photofile => {
                                                                                                    setFieldValue(
                                                                                                        'featuredImage',
                                                                                                        photofile
                                                                                                    );
                                                                                                }}
                                                                                            />
                                                                                        ) : null}
                                                                                        <Field name='visibility'>
                                                                                            {({
                                                                                                field,
                                                                                            }: FieldProps) => (
                                                                                                <label>
                                                                                                    <FormattedMessage
                                                                                                        id='ARTICLE_VISIBILITY'
                                                                                                        values={{
                                                                                                            gender: 'other',
                                                                                                        }}
                                                                                                    >
                                                                                                        {msg => (
                                                                                                            <span
                                                                                                                className={
                                                                                                                    styles.label
                                                                                                                }
                                                                                                            >
                                                                                                                {msg}
                                                                                                            </span>
                                                                                                        )}
                                                                                                    </FormattedMessage>
                                                                                                    <VisibilitySelect
                                                                                                        {...field}
                                                                                                    />
                                                                                                </label>
                                                                                            )}
                                                                                        </Field>
                                                                                        <SubmitButton
                                                                                            style={{
                                                                                                button: styles.button,
                                                                                            }}
                                                                                            disabled={
                                                                                                !isValid || isSubmitting
                                                                                            }
                                                                                        >
                                                                                            {(isEditing && (
                                                                                                <FormattedMessage
                                                                                                    id='PUBLISH_ARTICLE'
                                                                                                    values={{
                                                                                                        gender: 'v',
                                                                                                    }}
                                                                                                />
                                                                                            )) ||
                                                                                                undefined}
                                                                                        </SubmitButton>
                                                                                    </div>
                                                                                </Form>
                                                                            );
                                                                        }}
                                                                    </Formik>
                                                                </div>
                                                            )}
                                                        </Mutation>
                                                    )}
                                                </Mutation>
                                            )
                                        }
                                    </ViewerConsumer>
                                </div>
                            </React.Fragment>
                        )}
                    </AlertContext.Consumer>
                )}
            />
        );
    }
}

export default Styled(defaultStyles)(CreateEditArticle);
